import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SecurityQuestionsSetup = () => {
  const [catalog, setCatalog] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [picks, setPicks] = useState([
    { question_id: '', answer: '' },
    { question_id: '', answer: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    (async () => {
      const [r1, r2] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/auth/security-questions`),
        authFetch(`${BACKEND_URL}/api/auth/security-questions/mine`),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        setCatalog(d.questions || []);
      }
      if (r2.ok) {
        const d = await r2.json();
        setConfigured(!!d.configured);
        if (d.question_ids?.length === 2) {
          setPicks([
            { question_id: d.question_ids[0], answer: '' },
            { question_id: d.question_ids[1], answer: '' },
          ]);
        }
      }
    })();
  }, []);

  const save = async () => {
    setFlash(null);
    if (
      !picks[0].question_id ||
      !picks[1].question_id ||
      picks[0].question_id === picks[1].question_id
    ) {
      setFlash({ kind: 'err', text: 'Pick two different questions.' });
      return;
    }
    if (!picks[0].answer.trim() || !picks[1].answer.trim()) {
      setFlash({ kind: 'err', text: 'Answer both questions.' });
      return;
    }
    setSaving(true);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/auth/set-security-questions`,
        {
          method: 'POST',
          body: JSON.stringify({ pairs: picks }),
        },
      );
      if (!r.ok) {
        const d = await r.json();
        throw new Error(
          typeof d.detail === 'string' ? d.detail : 'Save failed',
        );
      }
      setConfigured(true);
      setFlash({ kind: 'ok', text: 'Security questions saved.' });
      setPicks((p) => p.map((x) => ({ ...x, answer: '' })));
    } catch (e) {
      setFlash({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const usedIds = new Set(picks.map((p) => p.question_id).filter(Boolean));

  return (
    <div
      className="p-5 rounded-2xl bg-slate-800/50 border border-slate-700"
      data-testid="security-questions-setup"
    >
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-indigo-300" />
        <h3 className="text-white font-semibold">Security Questions</h3>
        {configured && (
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-900/40 border border-emerald-700/50 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            Configured
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Used to recover your account if you forget your password. Pick two
        different questions.
      </p>

      {flash && (
        <div
          className={`mb-3 p-3 rounded-lg text-sm ${
            flash.kind === 'ok'
              ? 'bg-emerald-900/30 border border-emerald-700/50 text-emerald-200'
              : 'bg-red-900/30 border border-red-700/50 text-red-200'
          }`}
          data-testid="sq-flash"
        >
          {flash.text}
        </div>
      )}

      <div className="space-y-3">
        {picks.map((p, i) => (
          <div
            key={i}
            className="p-3 rounded-lg bg-slate-900/40 border border-slate-700"
          >
            <label className="text-xs text-gray-400">Question {i + 1}</label>
            <select
              value={p.question_id}
              onChange={(e) => {
                const v = e.target.value;
                setPicks((x) =>
                  x.map((row, idx) =>
                    idx === i ? { ...row, question_id: v } : row,
                  ),
                );
              }}
              className="w-full mt-1 bg-slate-800 border border-slate-600 text-white text-sm rounded-md px-3 py-2"
              data-testid={`sq-select-${i}`}
            >
              <option value="">— pick a question —</option>
              {catalog.map((q) => (
                <option
                  key={q.id}
                  value={q.id}
                  disabled={usedIds.has(q.id) && q.id !== p.question_id}
                >
                  {q.prompt}
                </option>
              ))}
            </select>

            <label className="text-xs text-gray-400 mt-2 block">Your answer</label>
            <Input
              type="text"
              value={p.answer}
              onChange={(e) =>
                setPicks((x) =>
                  x.map((row, idx) =>
                    idx === i ? { ...row, answer: e.target.value } : row,
                  ),
                )
              }
              placeholder={configured ? 'Type new answer to update' : ''}
              className="mt-1 bg-slate-900/60 border-slate-600 text-white"
              data-testid={`sq-answer-${i}`}
            />
          </div>
        ))}
      </div>

      <Button
        onClick={save}
        disabled={saving}
        className="w-full mt-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
        data-testid="sq-save-btn"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : configured ? (
          'Update security questions'
        ) : (
          'Save security questions'
        )}
      </Button>
    </div>
  );
};

export default SecurityQuestionsSetup;
